import { useEffect, useRef } from 'react'

import { useIntersection } from 'react-use'

import CategoryTextNodes from '@data/i18n/category.json'

import type {
	Breadcrumbs,
	CollectionProduct,
	CollectionProducts,
	PageInfo,
	SortByOptions
} from '@lib/types'
import { isEs } from '@lib/utils/lang'

import Spinner from '@components/Spinner'

import ProductCard from './ProductOrCollectionCard'
import SortProducts from './SortProducts'

type Props = {
	counter: string
	products: CollectionProducts
	breadcrumbs?: Breadcrumbs | null
	pageInfo: PageInfo
	isLoading: boolean
	sortByOptions: SortByOptions
	setSortByOptions: (sortByOptions: SortByOptions) => void
	setPageEndCursor: (param: PageInfo['endCursor']) => void
	favorites: string[] | null | undefined
	openMobileFilters: () => void
	isMemberMarket?: boolean
	quickShopProductIdSelected?: string | null
	setQuickShopProductIdSelected?: (id: string | null) => void
}

export default function ProductsList({
	counter,
	products,
	breadcrumbs,
	pageInfo,
	isLoading,
	sortByOptions,
	setSortByOptions,
	setPageEndCursor,
	favorites,
	openMobileFilters,
	isMemberMarket = false,
	quickShopProductIdSelected,
	setQuickShopProductIdSelected
}: Props) {
	const sentinelRef = useRef<HTMLDivElement>(null)
	const { isIntersecting } = useIntersection(sentinelRef, { rootMargin: '200px' }) || {}

	// Auto-load the next page when the sentinel scrolls into view (infinite scroll).
	// The `!isLoading` guard acts as the in-flight lock to prevent duplicate fetches.
	useEffect(() => {
		if (sentinelRef.current && isIntersecting && !isLoading && pageInfo?.hasNextPage) {
			setPageEndCursor(pageInfo?.endCursor ?? null)
		}
	}, [isIntersecting, isLoading, pageInfo, setPageEndCursor])

	return (
		<section className="flex-1 flex flex-col">
			<div className="flex justify-between px-4">
				<span className="font-medium  lg:text-xl">
					{isEs() &&
						`${counter} ${
							counter === '1' ? CategoryTextNodes.filter.result : CategoryTextNodes.filter.results
						}`}
					{!isEs() &&
						`${counter === '0' ? 'Filtered' : counter} ${
							counter === '1' ? CategoryTextNodes.filter.result : CategoryTextNodes.filter.results
						}`}
				</span>

				<button
					type="button"
					className="flex items-center text-xs font-medium lg:hidden gap-x-1"
					onClick={openMobileFilters}
				>
					{CategoryTextNodes.filter.filter}
					<i className="fa-solid fa-filter text-bbx-spark" />
				</button>

				<div className="max-lg:hidden ml-auto">
					<SortProducts sortByOptions={sortByOptions} setSortByOptions={setSortByOptions} />
				</div>
			</div>

			{products?.length > 0 ? (
				<div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-2 gap-y-6 mt-3">
					{products?.map((product: CollectionProduct) => (
						<ProductCard
							id={product.id}
							name={product.name}
							handle={product.handle}
							breadcrumbs={breadcrumbs}
							image={product.images[0]}
							hasStock={product.hasStock}
							avgRating={product.avgRating || undefined}
							numReviews={product.numReviews || undefined}
							price={product.price || undefined}
							compareAtPrice={product.compareAtPrice || undefined}
							vendor={product.vendor}
							key={product.id}
							type={product.type}
							isFavorite={favorites ? favorites?.includes(product.id) : undefined}
							collections={product.collections}
							isMemberMarket={isMemberMarket}
							quickShopProductIdSelected={quickShopProductIdSelected}
							setQuickShopProductIdSelected={setQuickShopProductIdSelected}
						/>
					))}
				</div>
			) : (
				<div className="flex flex-col items-center justify-center h-96">
					<i className="text-gray-300 fa-light fa-filter-slash text-5xl mb-6" />
					<span className="text-sm text-gray-400 font-medium">
						{CategoryTextNodes.products.empty}
					</span>
				</div>
			)}

			{products?.length > 0 && (
				<div
					ref={sentinelRef}
					className="mt-10 self-center flex min-h-[40px] items-center justify-center"
				>
					{pageInfo?.hasNextPage ? (
						isLoading && <Spinner />
					) : (
						<span role="status" className="text-sm text-gray-400 font-medium">
							{CategoryTextNodes.products.noMoreResults}
						</span>
					)}
				</div>
			)}
		</section>
	)
}
